import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import useIsFeatureEnabled from 'components/hooks/use_is_feature_enabled';
import virtualized from 'components/lib/virtualized';

const RESIZE_THROTTLE_MS = 300;

export class DebouncedAutosizerCache extends React.Component {
  constructor(props) {
    super(props);
    const { height, width } = props;

    this.state = {
      height,
      width,
    };

    this.frame = null;
    this.throttledSetSizeFromProps = _.throttle(this.setSizeFromProps, RESIZE_THROTTLE_MS);
  }

  componentDidUpdate() {
    if (this.props.height === this.state.height && this.props.width === this.state.width) {
      return;
    }
    if (this.props.isFrameCoalesced) {
      this.scheduleSizeFromProps();
    } else {
      this.throttledSetSizeFromProps();
    }
  }

  componentWillUnmount() {
    this.cancelScheduledSize();
    this.throttledSetSizeFromProps.cancel();
  }

  // A throttle interval is out of phase with the frame clock, so piled-up
  // measurements land as one jump — visible as the feed lagging a panel drag.
  scheduleSizeFromProps() {
    if (this.frame !== null) {
      return;
    }
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.setSizeFromProps();
    });
  }

  setSizeFromProps() {
    this.setState({
      height: this.props.height,
      width: this.props.width,
    });
  }

  cancelScheduledSize() {
    if (this.frame !== null) {
      cancelAnimationFrame(this.frame);
      this.frame = null;
    }
  }

  render() {
    const { height, width } = this.state;
    return this.props.children({ height, width });
  }
}

DebouncedAutosizerCache.propTypes = {
  children: PropTypes.func.isRequired,
  height: PropTypes.number,
  isFrameCoalesced: PropTypes.bool,
  width: PropTypes.number,
};

export default function DebouncedAutosizer({ children }) {
  const isFeatureEnabled = useIsFeatureEnabled();
  const isFrameCoalesced = isFeatureEnabled('sidePanelResize');

  return (
    <virtualized.AutoSizer>
      {({ height, width }) => (
        <DebouncedAutosizerCache height={height} isFrameCoalesced={isFrameCoalesced} width={width}>
          {children}
        </DebouncedAutosizerCache>
      )}
    </virtualized.AutoSizer>
  );
}

DebouncedAutosizer.propTypes = {
  children: PropTypes.func.isRequired,
};
